import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { ArrowLeft, Users, Filter, BarChart3 } from "lucide-react"
import {
  adminListUsers,
  applyAdminFilters,
  type AdminUserListRow,
  type AdminListFilters,
  type AdminFatigueBucket,
  type AdminSubscriptionFilter,
} from "@/lib/supabase"

const HOOPER_COLOR: Record<string, string> = {
  excellent: "#22c55e",
  bon: "#06b6d4",
  moyen: "#eab308",
  mauvais: "#f97316",
  "très mauvais": "#ef4444",
}

const ACWR_COLOR: Record<string, string> = {
  "sous-entrainement": "#eab308",
  optimal: "#22c55e",
  vigilance: "#f97316",
  surcharge: "#ef4444",
}

const SUB_COLOR: Record<string, string> = {
  active: "#22c55e",
  trialing: "#06b6d4",
  past_due: "#f97316",
  canceled: "#94a3b8",
  incomplete: "#f97316",
}

const SUB_LABEL: Record<string, string> = {
  active: "Actif",
  trialing: "Essai",
  past_due: "Échec paiement",
  canceled: "Résilié",
  incomplete: "Incomplet",
}

const SUB_OPTIONS: Array<{ value: AdminSubscriptionFilter; label: string }> = [
  { value: "all", label: "Tous statuts" },
  { value: "trialing", label: "Essai en cours" },
  { value: "trial-expired", label: "Essai terminé" },
  { value: "active", label: "Actifs" },
  { value: "canceled", label: "Résiliés" },
  { value: "no-sub", label: "Sans abo" },
]

const FATIGUE_OPTIONS: Array<{ value: AdminFatigueBucket; label: string }> = [
  { value: "all", label: "Toute fatigue" },
  { value: "good", label: "Forme bonne" },
  { value: "warning", label: "Forme moyenne" },
  { value: "bad", label: "Fatigue élevée" },
]

const INACTIVE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: "Toute activité" },
  { value: 7, label: "Inactifs > 7j" },
  { value: 14, label: "Inactifs > 14j" },
  { value: 30, label: "Inactifs > 30j" },
]

function formatDate(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" })
}

function formatRelativeDays(iso: string | null): string {
  if (!iso) return "Jamais"
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return "Aujourd'hui"
  if (days === 1) return "Hier"
  if (days < 30) return `Il y a ${days}j`
  if (days < 365) return `Il y a ${Math.floor(days / 30)} mois`
  return `Il y a ${Math.floor(days / 365)} an${Math.floor(days / 365) > 1 ? "s" : ""}`
}

const SELECT_STYLE = {
  background: "rgba(12,20,35,0.85)",
  border: "1px solid rgba(30,41,59,0.55)",
  color: "#e5e7eb",
}

export function AdminUsersPage() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUserListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [subFilter, setSubFilter] = useState<AdminSubscriptionFilter>("all")
  const [fatigueFilter, setFatigueFilter] = useState<AdminFatigueBucket>("all")
  const [inactiveDays, setInactiveDays] = useState<number>(0)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError(null)
    adminListUsers()
      .then((data) => {
        if (mounted) setUsers(data)
      })
      .catch((err: Error) => {
        if (mounted) setError(err.message)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [])

  const filtered = useMemo(() => {
    const filters: AdminListFilters = {
      subscription: subFilter,
      fatigue: fatigueFilter,
    }
    if (inactiveDays > 0) filters.inactiveDays = inactiveDays
    return applyAdminFilters(users, filters)
  }, [users, subFilter, fatigueFilter, inactiveDays])

  return (
    <div className="min-h-screen" style={{ background: "#060a13" }}>
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-5 pt-5 pb-3 sticky top-0 z-20"
        style={{ background: "rgba(6,10,19,0.92)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(30,41,59,0.4)" }}
      >
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate("/dashboard")}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-800/60 transition-colors cursor-pointer"
            style={{ background: "rgba(30,41,59,0.4)" }}
            aria-label="Retour au dashboard"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div className="flex-1">
            <h1 className="text-[18px] font-bold text-white tracking-tight flex items-center gap-2">
              <span style={{ color: "#06b6d4" }}>
                <Users className="w-4 h-4" />
              </span>
              Coach Dashboard
            </h1>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {loading ? "Chargement…" : `${filtered.length} / ${users.length} utilisateur${users.length > 1 ? "s" : ""}`}
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/stats")}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-800/60 transition-colors cursor-pointer"
            style={{ background: "rgba(30,41,59,0.4)" }}
            aria-label="Stats globales"
          >
            <BarChart3 className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* FILTRES */}
        <div className="flex items-center gap-2 mt-3">
          <span className="text-gray-500" aria-hidden>
            <Filter className="w-3.5 h-3.5" />
          </span>
          <select
            value={subFilter}
            onChange={(e) => setSubFilter(e.target.value as AdminSubscriptionFilter)}
            className="text-[11px] rounded-lg px-2 py-1.5 cursor-pointer flex-1 min-w-0"
            style={SELECT_STYLE}
          >
            {SUB_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={fatigueFilter}
            onChange={(e) => setFatigueFilter(e.target.value as AdminFatigueBucket)}
            className="text-[11px] rounded-lg px-2 py-1.5 cursor-pointer flex-1 min-w-0"
            style={SELECT_STYLE}
          >
            {FATIGUE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={inactiveDays}
            onChange={(e) => setInactiveDays(Number(e.target.value))}
            className="text-[11px] rounded-lg px-2 py-1.5 cursor-pointer flex-1 min-w-0"
            style={SELECT_STYLE}
          >
            {INACTIVE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* CONTENU */}
      <div className="px-4 pt-4 pb-10">
        {error && (
          <div
            className="rounded-xl p-4 mb-4 text-[13px]"
            style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5" }}
          >
            Erreur de chargement : {error}
          </div>
        )}

        {loading && !error && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-16 text-[13px] text-gray-500">
            Aucun utilisateur ne correspond aux filtres.
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <ul className="flex flex-col gap-2.5">
            {filtered.map((u, idx) => (
              <motion.li
                key={u.user_id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.02, 0.3) }}
              >
                <UserCard user={u} onClick={() => navigate(`/admin/users/${u.user_id}`)} />
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

interface UserCardProps {
  user: AdminUserListRow
  onClick: () => void
}

function UserCard({ user, onClick }: UserCardProps) {
  const subColor = user.subscription_status ? SUB_COLOR[user.subscription_status] ?? "#94a3b8" : "#94a3b8"
  const subLabel = user.subscription_status ? SUB_LABEL[user.subscription_status] ?? user.subscription_status : "Pas d'abo"
  const hooperColor = user.last_hooper_status ? HOOPER_COLOR[user.last_hooper_status] ?? "#94a3b8" : "#94a3b8"
  const acwrColor = user.last_acwr_zone ? ACWR_COLOR[user.last_acwr_zone] ?? "#94a3b8" : "#94a3b8"

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-2xl p-4 cursor-pointer transition-all hover:border-gray-700/80 active:scale-[0.99]"
      style={{ background: "rgba(12,20,35,0.6)", border: "1px solid rgba(30,41,59,0.55)" }}
    >
      {/* Ligne 1 : nom + badge sub */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-white truncate">{user.first_name}</p>
          <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
        </div>
        <span
          className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{ background: `${subColor}20`, color: subColor }}
        >
          {subLabel}
        </span>
      </div>

      {/* Ligne 2 : indicateurs santé */}
      <div className="flex items-center gap-3 text-[11px]">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ background: hooperColor }} />
          <span className="text-gray-400">Hooper</span>
          <span style={{ color: hooperColor, fontWeight: 600 }}>
            {user.last_hooper_status ?? "—"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ background: acwrColor }} />
          <span className="text-gray-400">ACWR</span>
          <span style={{ color: acwrColor, fontWeight: 600 }}>
            {user.last_acwr_zone ?? "—"}
          </span>
        </div>
      </div>

      {/* Ligne 3 : meta footer */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-800/40 text-[10px] text-gray-500">
        <span>Inscrit {formatDate(user.signup_at)}</span>
        <span>Dernière séance · {formatRelativeDays(user.last_session_date)}</span>
      </div>
    </button>
  )
}

